import React, { useState } from 'react';
import { Button, Space, message, Upload, Dropdown, Select, DatePicker } from 'antd';
import { ReloadOutlined, SaveOutlined, ExportOutlined, ImportOutlined, ClockCircleOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import DashboardSelector from './DashboardSelector';
import { useTheme } from '../../context/ThemeContext';

const { RangePicker } = DatePicker;

const TIME_RANGE_LABELS = {
  'now-5m': 'Last 5 minutes',
  'now-15m': 'Last 15 minutes',
  'now-30m': 'Last 30 minutes',
  'now-1h': 'Last 1 hour',
  'now-3h': 'Last 3 hours',
  'now-6h': 'Last 6 hours',
  'now-12h': 'Last 12 hours',
  'now-24h': 'Last 24 hours',
  'now-2d': 'Last 2 days',
  'now-7d': 'Last 7 days',
  'now-30d': 'Last 30 days',
};

const REFRESH_OPTIONS = [
  { label: 'Off', value: '' },
  { label: '5s', value: '5s' },
  { label: '10s', value: '10s' },
  { label: '30s', value: '30s' },
  { label: '1m', value: '1m' },
  { label: '5m', value: '5m' },
];

const DashboardHeader = ({
  dashboard,
  currentDashboardName,
  selectorRefreshKey,
  onSelectDashboard,
  onCreateNew,
  onUpdateDashboard,
  onSave,
  onExport,
  onImport,
  onRefresh,
  onBackToList,
  timeRange,
  onTimeRangeChange,
  refreshInterval = '',
  onRefreshIntervalChange,
  onAddPanel,
}) => {
  const { isDark } = useTheme();
  const [timePickerVisible, setTimePickerVisible] = useState(false);
  const [customRange, setCustomRange] = useState(null);

  // Parse timeRange to dayjs for RangePicker (when from/to are ISO strings)
  const getRangePickerValue = () => {
    const from = timeRange?.from;
    const to = timeRange?.to;
    if (!from || !to) return null;
    if (from === 'now' || (typeof from === 'string' && from.startsWith('now-')) || to === 'now') return customRange;
    const parse = (v) => (/^\d+$/.test(String(v)) ? dayjs(parseInt(v, 10)) : dayjs(v));
    const fromDate = parse(from);
    const toDate = parse(to);
    if (fromDate.isValid() && toDate.isValid()) return [fromDate, toDate];
    return customRange;
  };

  const handleSave = async () => {
    const result = await onSave();
    if (result?.success) {
      message.success('Dashboard saved');
    }
  };

  const handleExport = () => {
    const result = onExport();
    if (result.success) {
      const blob = new Blob([JSON.stringify(result.json, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${dashboard.title.replace(/\s+/g, '_')}_${Date.now()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      message.success('Dashboard exported successfully!');
    } else {
      message.error(`Failed to export: ${result.error}`);
    }
  };

  const handleImport = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target.result);
        const result = onImport(json);
        if (result.success) {
          message.success(`Dashboard imported successfully! (${result.panelCount} panels)`);
        } else {
          message.error(`Failed to import: ${result.error}`);
        }
      } catch (error) {
        message.error('Invalid JSON file');
      }
    };
    reader.readAsText(file);
    return false; // Prevent default upload
  };

  const quickRanges = [
    { label: 'Last 5 minutes', value: { from: 'now-5m', to: 'now' } },
    { label: 'Last 15 minutes', value: { from: 'now-15m', to: 'now' } },
    { label: 'Last 30 minutes', value: { from: 'now-30m', to: 'now' } },
    { label: 'Last 1 hour', value: { from: 'now-1h', to: 'now' } },
    { label: 'Last 3 hours', value: { from: 'now-3h', to: 'now' } },
    { label: 'Last 6 hours', value: { from: 'now-6h', to: 'now' } },
    { label: 'Last 12 hours', value: { from: 'now-12h', to: 'now' } },
    { label: 'Last 24 hours', value: { from: 'now-24h', to: 'now' } },
    { label: 'Last 2 days', value: { from: 'now-2d', to: 'now' } },
    { label: 'Last 7 days', value: { from: 'now-7d', to: 'now' } },
    { label: 'Last 30 days', value: { from: 'now-30d', to: 'now' } },
  ];

  const applyCustomRange = (dates) => {
    if (!dates || !dates[0] || !dates[1]) return;
    const from = dates[0].toISOString();
    const to = dates[1].toISOString();
    onTimeRangeChange({ from, to });
    setCustomRange(dates);
    setTimePickerVisible(false);
  };

  const quickRangeStyles = {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '8px 12px',
    fontSize: 13,
    borderRadius: 6,
    border: 'none',
    color: isDark ? '#e6edf3' : '#262626',
    background: 'transparent',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  };

  const timeRangeDropdown = (
    <div
      style={{
        padding: 0,
        width: 420,
        maxHeight: 'min(480px, 85vh)',
        overflow: 'auto',
        background: isDark ? '#161b22' : '#fff',
        borderRadius: 12,
        boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.5)' : '0 8px 24px rgba(0,0,0,0.12)',
        border: isDark ? '1px solid #30363d' : '1px solid #e8e8e8',
      }}
    >
      {/* Quick ranges - 2 columns, list style */}
      <div style={{ padding: '16px 16px 12px' }}>
        <div style={{
          fontSize: 11,
          fontWeight: 600,
          color: isDark ? '#6e7681' : '#8c8c8c',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: 10,
        }}>
          Quick select
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 2,
        }}>
          {quickRanges.map((range, index) => {
            const isSelected = timeRange?.from === range.value.from && timeRange?.to === range.value.to;
            return (
              <button
                key={index}
                type="button"
                onClick={() => {
                  onTimeRangeChange(range.value);
                  setCustomRange(null);
                  setTimePickerVisible(false);
                }}
                style={{
                  ...quickRangeStyles,
                  background: isSelected ? (isDark ? 'rgba(24, 144, 255, 0.15)' : 'rgba(24, 144, 255, 0.1)') : 'transparent',
                  color: isSelected ? (isDark ? '#58a6ff' : '#1890ff') : (isDark ? '#e6edf3' : '#262626'),
                }}
                onMouseOver={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = isDark ? '#21262d' : '#f5f5f5';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                {range.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Divider */}
      <div style={{
        height: 1,
        margin: '0 16px',
        background: isDark ? '#30363d' : '#f0f0f0',
      }} />

      {/* Custom date & time */}
      <div style={{ padding: '16px' }}>
        <div style={{
          fontSize: 11,
          fontWeight: 600,
          color: isDark ? '#6e7681' : '#8c8c8c',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: 12,
        }}>
          Custom range
        </div>
        <RangePicker
          showTime={{ format: 'HH:mm' }}
          format="YYYY-MM-DD HH:mm"
          value={getRangePickerValue()}
          onChange={applyCustomRange}
          placeholder={['Start date & time', 'End date & time']}
          style={{
            width: '100%',
            borderRadius: 8,
          }}
          presets={[
            { label: 'Last 1 hour', value: [dayjs().subtract(1, 'h'), dayjs()] },
            { label: 'Last 6 hours', value: [dayjs().subtract(6, 'h'), dayjs()] },
            { label: 'Last 24 hours', value: [dayjs().subtract(24, 'h'), dayjs()] },
            { label: 'Last 7 days', value: [dayjs().subtract(7, 'd'), dayjs()] },
          ]}
        />
      </div>
    </div>
  );

  const getTimeRangeLabel = () => {
    if (timeRange?.to === 'now' && timeRange?.from && TIME_RANGE_LABELS[timeRange.from]) {
      return TIME_RANGE_LABELS[timeRange.from];
    }
    if (timeRange?.from && timeRange?.to) {
      const from = timeRange.from;
      const to = timeRange.to;
      if (from.startsWith('2') && to.startsWith('2')) {
        try {
          const f = dayjs(from);
          const t = dayjs(to);
          if (f.isValid() && t.isValid()) {
            return `${f.format('MMM D, HH:mm')} – ${t.format('MMM D, HH:mm')}`;
          }
        } catch {}
      }
      return `${from} – ${to}`;
    }
    return 'Select time range';
  };

  return (
    <div
      style={{
        padding: '8px 16px',
        background: isDark ? '#161b22' : '#fff',
        borderBottom: isDark ? '1px solid #30363d' : '1px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        minHeight: 48,
      }}
    >
      {/* Left: Back, Dashboard selector, Title, Save/Export/Import, Add panel */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Button
          onClick={onBackToList}
          type="text"
          size="small"
          style={{ color: isDark ? '#8b949e' : '#8c8c8c', padding: '0 4px' }}
        >
          Back
        </Button>
        <span style={{ color: isDark ? '#484f58' : '#d9d9d9', marginRight: 4 }}>|</span>

        <DashboardSelector
          key={selectorRefreshKey}
          currentDashboard={currentDashboardName}
          refreshKey={selectorRefreshKey}
          onSelect={onSelectDashboard}
          onCreateNew={onCreateNew}
        />

        <Button onClick={handleSave} type="primary" icon={<SaveOutlined />} size="small" style={{ background: '#52c41a', borderColor: '#52c41a' }}>
          Save
        </Button>
        <Button onClick={handleExport} icon={<ExportOutlined />} size="small">
          Export
        </Button>
        <Upload accept=".json" showUploadList={false} beforeUpload={handleImport}>
          <Button icon={<ImportOutlined />} size="small">Import</Button>
        </Upload>

        {typeof onAddPanel === 'function' && (
          <>
            <span style={{ color: isDark ? '#484f58' : '#d9d9d9', marginLeft: 4 }}>|</span>
            <Button type="primary" icon={<PlusOutlined />} size="small" onClick={onAddPanel}>
              Add panel
            </Button>
          </>
        )}
      </div>

      {/* Right: Time range, Refresh interval, Refresh button (Grafana-style) */}
      <Space size={8}>
        <Dropdown
          dropdownRender={() => timeRangeDropdown}
          trigger={['click']}
          open={timePickerVisible}
          onOpenChange={setTimePickerVisible}
        >
          <Button
            size="small"
            icon={<ClockCircleOutlined />}
            style={{ color: isDark ? '#e6edf3' : undefined, borderColor: isDark ? '#30363d' : undefined }}
          >
            {getTimeRangeLabel()}
          </Button>
        </Dropdown>

        <Select
          size="small"
          value={refreshInterval === '' ? undefined : refreshInterval}
          onChange={(v) => onRefreshIntervalChange?.(v ?? '')}
          placeholder="Refresh"
          style={{ width: 90 }}
          options={REFRESH_OPTIONS}
          allowClear
        />

        <Button type="default" size="small" icon={<ReloadOutlined />} onClick={onRefresh}>
          Refresh
        </Button>
      </Space>
    </div>
  );
};

export default DashboardHeader;